import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { App, Button, ConfigProvider, Form, Input, Select, Space, Spin } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import MonacoEditor from '@monaco-editor/react';
import type { Monaco } from '@monaco-editor/react';
import Permission from '@/components/permission';
import {
    isServiceConfigVersionConflict,
    serviceConfigCreate,
    serviceConfigDetail,
    serviceConfigUpdate,
    type ServiceConfigItem,
} from '@/services/service-config';

import type { FormInstance } from 'antd/es/form';

const EDITOR_THEME_ID = 'service-config-editor-theme';
const EDITOR_MIN_HEIGHT = 320;
/** 编辑器与底部按钮之间的间距 */
const EDITOR_FOOTER_GAP = 24;
/** 页面底部与视口边缘的安全边距 */
const PAGE_BOTTOM_PADDING = 16;
const EDITOR_FONT_SIZE_STEPS = [12, 13, 14, 15, 16, 18, 20, 22, 24] as const;
const DEFAULT_EDITOR_FONT_SIZE = 14;

type EditorFontSize = (typeof EDITOR_FONT_SIZE_STEPS)[number];

type DetailPageMode = 'create' | 'edit' | 'detail';

const EditorFontSizeControl: React.FC<{
    value: EditorFontSize;
    onChange: (size: EditorFontSize) => void;
}> = ({ value, onChange }) => {
    const currentIndex = Math.max(0, EDITOR_FONT_SIZE_STEPS.indexOf(value));

    const stepBy = (delta: number) => {
        const next = EDITOR_FONT_SIZE_STEPS[currentIndex + delta];
        if (next !== undefined) {
            onChange(next);
        }
    };

    return (
        <Space.Compact
            size="small"
            onClick={(event) => event.preventDefault()}
            onMouseDown={(event) => event.preventDefault()}
        >
            <Button
                aria-label="decrease font size"
                disabled={currentIndex <= 0}
                icon={<MinusOutlined />}
                onClick={() => stepBy(-1)}
            />
            <Select
                options={EDITOR_FONT_SIZE_STEPS.map((size) => ({
                    label: `${size} px`,
                    value: size,
                }))}
                popupMatchSelectWidth={false}
                style={{ width: 92 }}
                value={value}
                onChange={onChange}
            />
            <Button
                aria-label="increase font size"
                disabled={currentIndex >= EDITOR_FONT_SIZE_STEPS.length - 1}
                icon={<PlusOutlined />}
                onClick={() => stepBy(1)}
            />
        </Space.Compact>
    );
};

type ConfigValueEditorProps = {
    value?: string;
    onChange?: (value: string) => void;
    height: number;
    fontSize: EditorFontSize;
    readOnly: boolean;
    beforeMount: (monaco: Monaco) => void;
    anchorRef: React.RefObject<HTMLDivElement | null>;
};

/** 承接 Form.Item 注入的 value/onChange，避免外层 div 截断受控值 */
const ConfigValueEditor: React.FC<ConfigValueEditorProps> = ({
    value,
    onChange,
    height,
    fontSize,
    readOnly,
    beforeMount,
    anchorRef,
}) => (
    <div ref={anchorRef}>
        <MonacoEditor
            beforeMount={beforeMount}
            height={`${height}px`}
            language="json"
            options={{
                minimap: { enabled: false },
                wordWrap: 'on',
                automaticLayout: true,
                readOnly,
                fontSize,
            }}
            theme={EDITOR_THEME_ID}
            value={value ?? ''}
            onChange={(nextValue) => onChange?.(nextValue ?? '')}
        />
    </div>
);

const useEditorTheme = () => {
    const themeReadyRef = useRef(false);

    return useCallback((monaco: Monaco) => {
        if (themeReadyRef.current) return;

        monaco.editor.defineTheme(EDITOR_THEME_ID, {
            base: 'vs',
            inherit: true,
            rules: [],
            colors: {
                'editor.background': '#f4f6fa',
            },
        });

        themeReadyRef.current = true;
    }, []);
};

/**
 * 编辑器高度 = 视口底部 - 编辑器顶部 - 底部按钮实际高度 - 间距
 * 保证取消/保存按钮始终留在首屏可见区域内。
 */
const useViewportEditorHeight = (ready: boolean) => {
    const anchorRef = useRef<HTMLDivElement>(null);
    const footerRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState(EDITOR_MIN_HEIGHT);

    useEffect(() => {
        if (!ready) return;

        const updateHeight = () => {
            const editorEl = anchorRef.current;
            const footerEl = footerRef.current;
            if (!editorEl || !footerEl) return;

            const top = editorEl.getBoundingClientRect().top;
            const footerHeight = footerEl.offsetHeight;
            const next = Math.max(
                EDITOR_MIN_HEIGHT,
                Math.floor(window.innerHeight - top - footerHeight - EDITOR_FOOTER_GAP - PAGE_BOTTOM_PADDING),
            );
            setHeight(next);
        };

        updateHeight();
        window.addEventListener('resize', updateHeight);
        const observer = new ResizeObserver(updateHeight);
        observer.observe(document.documentElement);
        observer.observe(footerRef.current);

        return () => {
            window.removeEventListener('resize', updateHeight);
            observer.disconnect();
        };
    }, [ready]);

    return { anchorRef, footerRef, height };
};

type FormValues = {
    key: string;
    module: string;
    type: ServiceConfigItem['type'];
    value: string;
};

const DEFAULT_FORM_VALUE: FormValues = {
    key: '',
    module: '',
    type: 'public',
    value: JSON.stringify({ key: 'value' }, null, 2),
};

const toFormValues = (record: Pick<ServiceConfigItem, 'key' | 'module' | 'type' | 'value'>): FormValues => ({
    key: record.key,
    module: record.module ?? '',
    type: record.type,
    value: formatConfigValue(record.value),
});

function formatConfigValue(value: ServiceConfigItem['value']): string {
    if (value === null || value === undefined) {
        return JSON.stringify({}, null, 2);
    }

    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            return JSON.stringify(parsed, null, 2);
        } catch {
            return value;
        }
    }

    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
}

const ServiceConfigDetail: React.FC = () => {
    const { message } = App.useApp();
    const { formatMessage } = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [form] = Form.useForm<FormValues>();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editorFontSize, setEditorFontSize] = useState<EditorFontSize>(DEFAULT_EDITOR_FONT_SIZE);

    /**
     * 乐观锁版本号：
     * 进入编辑/详情页时从 record 或接口中记录下来，保存时随 payload 一并提交。
     * 不放进 form 是为了避免被用户无意修改。
     */
    const versionRef = useRef<number | undefined>(undefined);

    const configId = searchParams.get('id');
    const rawMode = searchParams.get('mode') as DetailPageMode | null;
    const handleBeforeMount = useEditorTheme();
    const { anchorRef: editorAnchorRef, footerRef, height: editorHeight } = useViewportEditorHeight(!loading);

    const stateRecord = useMemo(() => {
        const state = location.state as { record?: ServiceConfigItem } | null;
        return state?.record;
    }, [location.state]);

    const pageMode = useMemo<DetailPageMode>(() => {
        if (rawMode === 'create' || rawMode === 'edit' || rawMode === 'detail') {
            return rawMode;
        }
        return configId ? 'edit' : 'create';
    }, [configId, rawMode]);

    const isCreateMode = pageMode === 'create';
    const isReadOnlyMode = pageMode === 'detail';

    const permissionScope = 'System-Management_Configs';
    const listPath = '/system-management/service-config';

    const loadDetail = useCallback(
        async (formRef: FormInstance<FormValues>) => {
            formRef.resetFields();
            versionRef.current = undefined;

            if (pageMode === 'create') {
                formRef.setFieldsValue(DEFAULT_FORM_VALUE);
                return;
            }

            if (stateRecord) {
                formRef.setFieldsValue(toFormValues(stateRecord));
                versionRef.current = stateRecord.version;
                // state 里若未带 version（来自旧接口），仍走一次详情接口补拿，保证乐观锁可用
                if (stateRecord.version === undefined && configId) {
                    try {
                        const { data } = await serviceConfigDetail(configId);
                        if (data.code === 200 && data.data) {
                            versionRef.current = data.data.version;
                        }
                    } catch {
                        // 静默失败：后续保存冲突时仍会兜底提示
                    }
                }
                return;
            }

            if (!configId) {
                return;
            }

            setLoading(true);
            try {
                const { data } = await serviceConfigDetail(configId);
                if (data.code !== 200 || !data.data) {
                    message.error(data.msg);
                    return;
                }
                formRef.setFieldsValue(toFormValues(data.data));
                versionRef.current = data.data.version;
            } catch (error: any) {
                message.error(error?.response?.data?.msg || error?.message || 'Failed to fetch config detail');
            } finally {
                setLoading(false);
            }
        },
        [configId, message, pageMode, stateRecord],
    );

    useEffect(() => {
        loadDetail(form);
    }, [form, loadDetail]);

    const handleSubmit = useCallback(async () => {
        if (isReadOnlyMode) {
            return;
        }

        try {
            const values = await form.validateFields();
            let parsedValue: any = {};
            try {
                parsedValue = values.value ? JSON.parse(values.value) : {};
            } catch (parseError) {
                message.error(formatMessage({ id: 'serviceConfig.form.value.invalidJson' }));
                return;
            }

            const payloadBase = {
                key: values.key,
                module: values.module?.trim() ?? '',
                type: values.type,
                value: parsedValue,
            };

            setSaving(true);
            if (!isCreateMode && configId) {
                const { data } = await serviceConfigUpdate(configId, {
                    ...payloadBase,
                    version: versionRef.current,
                });

                if (isServiceConfigVersionConflict(data)) {
                    message.warning(formatMessage({ id: 'serviceConfig.update.conflict' }));
                    await loadDetail(form);
                    return;
                }

                if (data.code !== 200) {
                    message.error(data.msg);
                    return;
                }

                // 成功后刷新本地版本号，支持用户连续保存
                if (data.data?.version !== undefined) {
                    versionRef.current = data.data.version;
                }
                message.success(formatMessage({ id: 'serviceConfig.update.success' }));
            } else {
                const { data } = await serviceConfigCreate(payloadBase);
                if (data.code !== 200) {
                    message.error(data.msg);
                    return;
                }
                message.success(formatMessage({ id: 'serviceConfig.create.success' }));
            }
            navigate(listPath);
        } catch (error: any) {
            if (error?.errorFields) {
                return;
            }

            if (isServiceConfigVersionConflict(error)) {
                message.warning(formatMessage({ id: 'serviceConfig.update.conflict' }));
                await loadDetail(form);
                return;
            }

            message.error(error?.response?.data?.msg || error?.message || 'Submit failed');
        } finally {
            setSaving(false);
        }
    }, [configId, form, formatMessage, isCreateMode, isReadOnlyMode, listPath, loadDetail, message, navigate]);

    const pageTitle = useMemo(() => {
        if (pageMode === 'detail') {
            return <FormattedMessage id="serviceConfig.detail.viewTitle" />;
        }
        if (pageMode === 'edit') {
            return <FormattedMessage id="serviceConfig.detail.editTitle" />;
        }
        return <FormattedMessage id="serviceConfig.detail.createTitle" />;
    }, [pageMode]);

    const mutationPermission = isCreateMode ? `${permissionScope}_Create` : `${permissionScope}_Update`;

    return (
        <Permission permission={permissionScope}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24, width: '100%' }}>
                <Space align="center" split={<span>/</span>}>
                    <Button type="link" onClick={() => navigate(-1)}>
                        <FormattedMessage id="b.back" />
                    </Button>
                    {pageTitle}
                </Space>
                <Spin spinning={loading}>
                    <Form disabled={isReadOnlyMode} form={form} layout="vertical" preserve={false}>
                        <Form.Item
                            name="key"
                            label={<FormattedMessage id="serviceConfig.form.key" />}
                            rules={[
                                { required: true, message: formatMessage({ id: 'serviceConfig.form.key.required' }) },
                            ]}
                        >
                            <Input
                                disabled={isReadOnlyMode}
                                placeholder={formatMessage({ id: 'serviceConfig.form.key.placeholder' })}
                            />
                        </Form.Item>
                        <Form.Item
                            name="module"
                            label={<FormattedMessage id="serviceConfig.form.module" />}
                            rules={[
                                {
                                    required: true,
                                    message: formatMessage({ id: 'serviceConfig.form.module.required' }),
                                },
                                {
                                    validator: (_, value) => {
                                        if (typeof value === 'string' && value.trim()) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(
                                            new Error(formatMessage({ id: 'serviceConfig.form.module.required' })),
                                        );
                                    },
                                },
                            ]}
                        >
                            <Input
                                disabled={isReadOnlyMode}
                                placeholder={formatMessage({ id: 'serviceConfig.form.module.placeholder' })}
                            />
                        </Form.Item>
                        <Form.Item
                            name="type"
                            label={<FormattedMessage id="serviceConfig.form.type" />}
                            rules={[
                                { required: true, message: formatMessage({ id: 'serviceConfig.form.type.required' }) },
                            ]}
                        >
                            <Select
                                options={[
                                    { label: formatMessage({ id: 'serviceConfig.type.public' }), value: 'public' },
                                    { label: formatMessage({ id: 'serviceConfig.type.private' }), value: 'private' },
                                ]}
                            />
                        </Form.Item>
                        <Form.Item
                            name="value"
                            label={
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 12,
                                        width: '100%',
                                    }}
                                >
                                    <FormattedMessage id="serviceConfig.form.value" />
                                    <ConfigProvider componentDisabled={false}>
                                        <EditorFontSizeControl value={editorFontSize} onChange={setEditorFontSize} />
                                    </ConfigProvider>
                                </span>
                            }
                            rules={[
                                { required: true, message: formatMessage({ id: 'serviceConfig.form.value.required' }) },
                            ]}
                            style={{ marginBottom: 0 }}
                            styles={{ label: { width: '100%' } }}
                        >
                            <ConfigValueEditor
                                anchorRef={editorAnchorRef}
                                beforeMount={handleBeforeMount}
                                fontSize={editorFontSize}
                                height={editorHeight}
                                readOnly={isReadOnlyMode}
                            />
                        </Form.Item>
                    </Form>
                </Spin>
                <div ref={footerRef}>
                    <Space>
                        <Button onClick={() => navigate(listPath)}>
                            <FormattedMessage id="b.cancel" />
                        </Button>
                        {!isReadOnlyMode && (
                            <Permission permission={mutationPermission}>
                                <Button loading={saving} type="primary" onClick={handleSubmit}>
                                    <FormattedMessage id="b.save" />
                                </Button>
                            </Permission>
                        )}
                    </Space>
                </div>
            </div>
        </Permission>
    );
};

export default ServiceConfigDetail;
